import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCallback, useContext, useMemo, useState } from 'react';
import { useClickOutside } from '../../../hooks/common/useClickOutside';
import { strip } from '../../../utilities/strings';
import FormControlWrapper from '../FormControlWrapper';
import { createComboboxContext, resolveSelected } from './Combobox.helpers';
import { usePrepareData } from './Combobox.hooks';
import ComboboxInput from './ComboboxInput';
import ComboboxList from './ComboboxList';
import ComboboxPillList from './ComboboxPillList';
var ComboboxContext = createComboboxContext();
export function useComboboxContext() {
    return useContext(ComboboxContext);
}
function Combobox(_a) {
    var id = _a.id, htmlName = _a.htmlName, label = _a.label, value = _a.value, data = _a.data, onAdd = _a.onAdd, addItemLabel = _a.addItemLabel, onChange = _a.onChange, useGrouping = _a.useGrouping, _b = _a.skipSort, skipSort = _b === void 0 ? false : _b, useMultipleSelect = _a.useMultipleSelect, usePillList = _a.usePillList, useAutocomplete = _a.useAutocomplete, _c = _a.placeholder, placeholder = _c === void 0 ? useAutocomplete ? 'Type to search for a value' : 'Please select a value' : _c, disabled = _a.disabled, required = _a.required, labelProps = _a.labelProps, wrapperProps = _a.wrapperProps, className = _a.className, listClassName = _a.listClassName, inputClassName = _a.inputClassName, tooltip = _a.tooltip, testSelector = _a.testSelector, error = _a.error, clearable = _a.clearable, useSrOnlyLabel = _a.useSrOnlyLabel, alignDropdown = _a.alignDropdown, noMatchLabel = _a.noMatchLabel, handleSelect = _a.handleSelect;
    var _d = useState(''), query = _d[0], setQuery = _d[1];
    var _e = useState(false), isOpen = _e[0], setIsOpen = _e[1];
    var preppedData = usePrepareData(query, data, useGrouping, skipSort);
    var clickOutsideRef = useClickOutside(function () { return setIsOpen(false); });
    var hasNoMatches = useMemo(function () { return !!useAutocomplete && !!query && !!query.length && (!preppedData || !preppedData.length); }, [query, preppedData]);
    var handleChange = useCallback(function (selected, removeSelected) {
        if (removeSelected === void 0) { removeSelected = false; }
        if (!selected) {
            onChange(selected);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            !useMultipleSelect && setIsOpen(false);
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !useMultipleSelect && setIsOpen(false);
        var castSelected = Array.isArray(selected) ? selected : selected;
        var castValue = Array.isArray(value) ? value : value;
        onChange(resolveSelected(castValue, castSelected, removeSelected, useMultipleSelect));
    }, [value, onChange]);
    var context = useMemo(function () { return ({
        onChange: handleChange,
        value: value,
        isOpen: isOpen,
        openMenu: function () { return setIsOpen(true); },
        closeMenu: function () { return setIsOpen(false); },
        selectAll: function () { return onChange(data); },
        onAdd: function () { return onAdd === null || onAdd === void 0 ? void 0 : onAdd(); },
        addItemLabel: addItemLabel,
        areAllSelected: (value === null || value === void 0 ? void 0 : value.length) === (data === null || data === void 0 ? void 0 : data.length),
        useGrouping: useGrouping,
        useMultipleSelect: useMultipleSelect,
        usePillList: usePillList,
        useAutocomplete: useAutocomplete,
        testSelector: testSelector ? strip(testSelector) : "combobox-".concat(strip(label)),
        clearable: clearable,
        handleSelect: handleSelect,
    }); }, [
        onChange,
        data,
        value,
        isOpen,
        useGrouping,
        useMultipleSelect,
        usePillList,
        useAutocomplete,
        onAdd,
        addItemLabel,
        handleSelect,
    ]);
    return (_jsxs(ComboboxContext.Provider, { value: context, children: [_jsx(FormControlWrapper, { id: id, htmlName: htmlName, label: label, labelProps: labelProps, wrapperProps: wrapperProps, required: required, tooltip: tooltip, error: error, srLabel: useSrOnlyLabel, children: _jsxs("div", { className: clsx('relative', className), ref: clickOutsideRef, "data-cy": context.testSelector, children: [_jsx(ComboboxInput, { value: value, query: query, setQuery: setQuery, placeholder: placeholder, disabled: disabled, useSrOnlyLabel: useSrOnlyLabel, inputClassName: inputClassName, htmlName: htmlName }), _jsx(ComboboxList, { alignDropdown: alignDropdown, data: preppedData, hasNoMatches: hasNoMatches, noMatchLabel: noMatchLabel, className: listClassName })] }) }), usePillList && _jsx(ComboboxPillList, {})] }));
}
export { Combobox as default };
